import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { Button, IconButton } from './ui';
import { BlackFridayBadge } from './BlackFridayBadge';
import { useTranslation } from '../../lib/i18n';
import { useGenericBi } from '../../lib/use-generic-bi';
import { useItemSeenBi } from '../../lib/use-item-seen-bi';
import { routerHandlesClick, type ClickModifiers } from '../../lib/use-in-app-link';
import { useLeaveChatOnNavigate } from '../../lib/use-leave-chat';
import { skipChatUrlSyncOnce } from './FloatingChat/use-chat-url';
import { upgradeHref } from '../../lib/upgrade-origin';
import { useBlackFridayRedeem } from '../../lib/use-black-friday-redeem';
import {
  BLACK_FRIDAY_BANNER_SOURCE,
  formatBlackFridayCountdown,
  type BlackFridayBannerState,
} from '../../lib/black-friday';
import styles from './BlackFridayBanner.module.css';

const PAGE = 'black_friday_banner';

/**
 * The Black Friday band — five states in one bar (Figma 4217:25450, mobile 4565:57247).
 *
 * | state | says | CTA |
 * |---|---|---|
 * | `offer` | Your Black Friday plan is ready for you. **Get unlimited credits FREE for 48 hours** | Unlock now — redeems `blackfriday2026` |
 * | `running` | Free unlimited credits: `47:57:37` | none |
 * | `final` | Final hours of unlimited credits: `05:57:37` | none |
 * | `ended-plans` | Your 48 free hours are over… Upgrade to continue growing your business. | See plans → `/packages` |
 * | `ended-wins` | Your 48 free hours are over… Continue growing your business. | See your wins → `/wins`, then the band turns into `ended-plans` |
 *
 * Which one shows is `useBlackFriday`; this draws it. The per-second tick lives in that hook so only the
 * band repaints each second, not the frame — and so does what the X means per state (persisted on the
 * offer and the over-bands, this-visit-only on the countdowns), because the frame has to see the band go
 * before it hands the top of the screen back to the bands this one outranks.
 */
export function BlackFridayBanner({
  state,
  untilIso,
  narrow,
  onDismiss,
  onOpenWins,
}: {
  state: Exclude<BlackFridayBannerState, 'hidden'>;
  untilIso: string | null;
  narrow: boolean;
  /** Persists the dismissal for the offer and the over-bands. The countdowns dismiss in place. */
  onDismiss: () => void;
  /** Persists that the wins were opened, so the band turns into "See plans". */
  onOpenWins: () => void;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const seenRef = useItemSeenBi('black-friday-banner', { additionalFields: { banner_state: state } });
  const barRef = useRef<HTMLDivElement | null>(null);
  const probeRef = useRef<HTMLDivElement | null>(null);
  const attachBar = useCallback(
    (node: HTMLDivElement | null) => {
      barRef.current = node;
      seenRef(node);
    },
    [seenRef],
  );
  // The countdowns read the same copy at every width, so only the offer and the over-bands have a
  // shorter sentence to fall back to.
  const hasShortCopy = state !== 'running' && state !== 'final';
  const [short, setShort] = useState(false);
  const redeem = useBlackFridayRedeem(BLACK_FRIDAY_BANNER_SOURCE);
  const leaveChat = useLeaveChatOnNavigate();
  // Both destinations are real anchors (`Button href` renders a Next <Link> that resolves the active
  // mount itself), so a cmd/middle-click opens them in a new tab as every other link in the app does.

  const click = (buttonName: string) =>
    logPageClickBi({ page_name: PAGE, button_name: buttonName, origin: PAGE }, { additionalFields: { banner_state: state } });

  const handleDismiss = () => {
    click('black_friday_banner_dismiss');
    onDismiss();
  };

  const onUnlock = () => {
    click('black_friday_banner_unlock');
    redeem.redeem();
  };

  const onSeePlans = (event?: ClickModifiers) => {
    click('black_friday_banner_see_plans');
    // Only for a same-tab navigation — a modified/middle click opens the picker in a NEW tab, so this
    // tab never navigates and its own chat-collapse still needs the sync this would suppress.
    if (routerHandlesClick(event ?? {})) skipChatUrlSyncOnce();
    leaveChat();
  };

  const onSeeWins = (event?: ClickModifiers) => {
    click('black_friday_banner_see_wins');
    if (routerHandlesClick(event ?? {})) skipChatUrlSyncOnce();
    leaveChat();
    onOpenWins();
  };

  const cta = (() => {
    if (state === 'offer') {
      return (
        <Button color="accent" size={narrow ? 'small' : 'medium'} loading={redeem.busy} disabled={!redeem.canPress} onClick={onUnlock}>
          {t('blackFriday.banner.cta.unlock')}
        </Button>
      );
    }
    if (state === 'ended-plans') {
      return (
        <Button color="accent" size={narrow ? 'small' : 'medium'} href={upgradeHref('black_friday_banner')} onClick={onSeePlans}>
          {t('blackFriday.banner.cta.plans')}
        </Button>
      );
    }
    if (state === 'ended-wins') {
      return (
        <Button color="accent" size={narrow ? 'small' : 'medium'} href="/wins" onClick={onSeeWins}>
          {t('blackFriday.banner.cta.wins')}
        </Button>
      );
    }
    // The two countdowns carry no CTA — the design draws none, and there is nothing left to ask for.
    return null;
  })();

  const countdown = (() => {
    if (state !== 'running' && state !== 'final') return null;
    const remaining = untilIso ? new Date(untilIso).getTime() - Date.now() : 0;
    return (
      <>
        <span>{t(state === 'final' ? 'blackFriday.banner.final' : 'blackFriday.banner.running')}</span>{' '}
        <span className={styles.counter}>{formatBlackFridayCountdown(remaining)}</span>
      </>
    );
  })();

  const label = (() => {
    if (state === 'offer') {
      return (
        <>
          <span>{t('blackFriday.banner.offer.lead')}</span>{' '}
          <span className={styles.emphasis}>{t('blackFriday.banner.offer.emphasis')}</span>
        </>
      );
    }
    if (countdown) return countdown;
    return <span>{t(state === 'ended-plans' ? 'blackFriday.banner.ended.plans' : 'blackFriday.banner.ended.wins')}</span>;
  })();

  // The strip says LESS than the bar, and that is the design rather than a truncation (Figma
  // 4565:56583): the offer drops its lead sentence and keeps the promise, and both over-bands say only
  // that the window has closed — their CTA is what still tells the two apart. The countdowns are short
  // enough already and read the same copy on both.
  const compactLabel = (() => {
    if (state === 'offer') return <span>{t('blackFriday.banner.offer.emphasis')}</span>;
    if (countdown) return countdown;
    return <span>{t('blackFriday.banner.ended.compact')}</span>;
  })();

  // Desktop says the short copy instead of wrapping. Where the full row stops fitting differs per locale
  // and per state, so it is measured rather than a breakpoint. The hidden probe always holds the FULL row
  // on one line; measuring the visible row instead would flip back as soon as it swapped.
  useLayoutEffect(() => {
    const bar = barRef.current;
    const probe = probeRef.current;
    if (narrow || !hasShortCopy || !bar || !probe) {
      setShort(false);
      return;
    }
    const update = () => {
      const cs = getComputedStyle(bar);
      const room = bar.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
      setShort(probe.getBoundingClientRect().width > room);
    };
    update();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const ro = new ResizeObserver(update);
    ro.observe(bar);
    ro.observe(probe);
    return () => ro.disconnect();
  }, [narrow, hasShortCopy]);

  // Numbers are grouped CLIENT-side and interpolated as strings — `t` takes no number formatting, and
  // the server would group them its own way. Same step `RedeemCodeEntry` takes.
  const messageValues = redeem.message?.values
    ? Object.fromEntries(Object.entries(redeem.message.values).map(([k, v]) => [k, v.toLocaleString()]))
    : undefined;
  const message = redeem.message ? (
    <span className={`${styles.message} ${redeem.message.tone === 'error' ? styles.messageError : ''}`} role="status">
      {t(redeem.message.key, messageValues)}
    </span>
  ) : null;

  const close = (
    <IconButton size="tiny" variant="text" aria-label={t('blackFriday.banner.dismiss')} onClick={handleDismiss}>
      <X size={16} strokeWidth={2} aria-hidden />
    </IconButton>
  );

  // Mark on top, the sentence under it, the CTA at the foot — one centred column, where the desktop bar
  // runs all three across one line.
  if (narrow) {
    return (
      <div ref={seenRef} className={styles.mobileBar}>
        <div className={styles.mobileContent}>
          <BlackFridayBadge />
          <span className={styles.mobileText}>{compactLabel}</span>
          {cta}
          {message}
        </div>
        <span className={styles.mobileCloseSlot}>{close}</span>
      </div>
    );
  }

  return (
    <div ref={attachBar} className={styles.bar}>
      <div className={styles.content}>
        <BlackFridayBadge />
        <span className={styles.line}>
          <span className={styles.text}>{short ? compactLabel : label}</span>
          {cta}
        </span>
      </div>
      {message}
      <span className={styles.closeSlot}>{close}</span>
      {hasShortCopy && (
        <div className={styles.probeClip} aria-hidden inert>
          <div ref={probeRef} className={`${styles.content} ${styles.probe}`}>
            <BlackFridayBadge />
            <span className={styles.line}>
              <span className={styles.text}>{label}</span>
              {cta}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
